<template>
  <div class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
    <div class="w-full max-w-lg dashboard-panel !bg-slate-900 p-6 relative max-h-[90vh] overflow-y-auto">
      <button
        @click="emit('close')"
        class="absolute top-4 right-4 text-slate-400 hover:text-white"
        aria-label="Close"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <h2 class="text-xl font-bold text-white mb-2">Manage Users</h2>
      <p class="text-gray-200 text-sm mb-6">Add new users who can access the dashboard.</p>

      <form @submit.prevent="handleAddUser" class="space-y-4 mb-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label for="new-user-email" class="block text-sm font-medium text-white mb-1">Email</label>
            <input
              id="new-user-email"
              v-model="newEmail"
              type="email"
              required
              class="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="user@example.com"
            />
          </div>
          <div>
            <label for="new-user-password" class="block text-sm font-medium text-white mb-1">Password</label>
            <input
              id="new-user-password"
              v-model="newPassword"
              type="password"
              required
              minlength="6"
              class="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="••••••••"
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="is-admin"
            v-model="isAdmin"
            type="checkbox"
            class="h-4 w-4 rounded border-slate-600 bg-slate-800 text-primary focus:ring-primary"
          />
          <label for="is-admin" class="text-sm text-white font-medium">Admin user</label>
        </div>

        <div v-if="error" class="bg-red-500/10 border border-red-500/30 text-red-100 rounded-lg p-3 text-sm font-medium">
          {{ error }}
        </div>
        <div v-if="success" class="bg-green-500/10 border border-green-500/30 text-green-100 rounded-lg p-3 text-sm font-medium">
          {{ success }}
        </div>

        <button
          type="submit"
          :disabled="loading"
          class="w-full h-10 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          {{ loading ? 'Adding...' : 'Add User' }}
        </button>
      </form>

      <h3 class="text-lg font-semibold text-white mb-3">Existing Users</h3>
      <div v-if="usersLoading" class="text-gray-200 text-sm">Loading users...</div>
      <ul v-else class="space-y-2">
        <li
          v-for="u in users"
          :key="u.id"
          class="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700"
        >
          <div>
            <p class="text-white text-sm font-medium">{{ u.email }}</p>
            <p class="text-gray-200 text-xs">
              {{ u.is_admin ? 'Admin' : 'User' }} &middot; {{ u.is_active ? 'Active' : 'Inactive' }}
            </p>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useAuth } from '../composables/useAuth.js'

const emit = defineEmits(['close'])

const newEmail = ref('')
const newPassword = ref('')
const isAdmin = ref(false)
const users = ref([])
const usersLoading = ref(false)
const success = ref('')

const { createUser, listUsers, loading, error } = useAuth()

const loadUsers = async () => {
  usersLoading.value = true
  try {
    users.value = await listUsers()
  } catch (err) {
    console.error('Failed to load users:', err)
  } finally {
    usersLoading.value = false
  }
}

const handleAddUser = async () => {
  error.value = null
  success.value = ''
  try {
    await createUser(newEmail.value, newPassword.value, isAdmin.value)
    success.value = `User ${newEmail.value} created successfully.`
    newEmail.value = ''
    newPassword.value = ''
    isAdmin.value = false
    await loadUsers()
  } catch {
    // error is already set by the composable
  }
}

onMounted(loadUsers)
</script>
